import { Button, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import SyButton from '../Common/SyButton';

const useStyles = makeStyles({
    button: {
        width: '100%',
        justifyContent: 'flex-start',
        // reduce vertical padding by 1.5px (top + bottom = -3px total height)
        padding: `calc(${tokens.spacingVerticalM} - 2px) ${tokens.spacingHorizontalL}`,
        '&:hover': {
            background: 'var(--menu-button-hover-bg) !important',
        },
    },
    buttonNoHover: {
        width: '100%',
        justifyContent: 'flex-start',
        // reduce vertical padding by 1.5px (top + bottom = -3px total height)
        padding: `calc(${tokens.spacingVerticalM} - 2px) ${tokens.spacingHorizontalL}`,
        '&:hover': {
            background: 'transparent !important',
        },
    },
});

interface AppMenuButtonProps {
    onClick: () => void;
    label: string;
    title: string;
    icon: JSX.Element;
    disabled?: boolean;
    color?: string;
    changeBackgroundOnHover?: boolean;
}
const AppMenuButton: FC<AppMenuButtonProps> = (props) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { onClick, label, title, icon, disabled, color, changeBackgroundOnHover = true } = props;
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
        }
    };

    const sizedIcon = React.isValidElement(icon)
    ? React.cloneElement(icon as React.ReactElement, { 
        width: 16, 
        height: 16, 
        strokeWidth: 2,
        style: { 
            color: color ?? undefined,
            stroke: color ?? undefined,
            strokeOpacity: 1, 
            opacity: 1 
        }
    } as unknown as Record<string, unknown>)
        : icon;

    return (
        <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            title={t(title)}
            aria-disabled={disabled}
            onClick={() => { if (!disabled) onClick(); }}
            onKeyDown={handleKeyDown}
            className={changeBackgroundOnHover ? classes.button : classes.buttonNoHover}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                userSelect: 'none',
                borderRadius: tokens.borderRadiusLarge,
                color: color ?? tokens.colorNeutralForeground1,
            }}
        >
            <span style={{ display: 'inline-flex', width: 16, height: 16, alignItems: 'center', justifyContent: 'center' }}>
                {sizedIcon}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', height: '100%' }}>
                {t(label)}</span>
        </div>
    );
};

export default AppMenuButton;
